import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate, useLocation, useNavigationType } from 'react-router-dom';
import { Icon, MenuList, ToastContainer } from 'pepsico-ds';
import { appConstants } from '../../constants/appconstants';
import { getUserClaims, getAuthHeader } from '../../utils/auth/unified-auth';
import { MISessionContext } from './MISessionContext';
import MIChatActiveView from './MIChatActiveView';
import './MarketIntelligenceChat.scss';
import './SpendAnalyticsAgent.scss';
import './SpendAnalyticsConversations.scss';
import {
  normalizeSessionHistory,
  mapSessionSummaryItem,
  extractInsightsBlock,
  normalizeSessionMessages,
  getSpendAnalyticsCardVisibilityFromEnv,
} from './chatUtils';
import { SourceQueryItem, CitationItem } from './ChatSourceItems';
import { useDispatch } from 'react-redux';
import { FetchUserInfoAction } from '../../redux/action/LandingPageAction';

let toastIdCounter = 0;

const MIConversations = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();
  const sessionContext = useContext(MISessionContext);
  const dispatch = useDispatch();
  const [userInfo, setUserInfo] = useState({});
  const [isUserDataFetched, setIsUserDataFetched] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(false);

  const hasNavigationState =
    location.state !== null && location.state !== undefined;
  const initialMessage = location.state?.message || '';
  const initialSessionId = location.state?.session_id || null;
  const initialThreadId =
    location.state?.conversation_id || location.state?.thread_id || null;

  const [message, setMessage] = useState('');
  const [showSidebar, setShowSidebar] = useState(true);
  const [showRightSidebar, setShowRightSidebar] = useState(false);
  const [activeSqlQueries, setActiveSqlQueries] = useState([]);
  const [activeCitations, setActiveCitations] = useState([]);
  const [expandedSourceItems, setExpandedSourceItems] = useState({});
  const [messages, setMessages] = useState([]);
  const [isThinking, setIsThinking] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [showAgentMenu, setShowAgentMenu] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [threadId, setThreadId] = useState(null);
  const [lastProcessedMessage, setLastProcessedMessage] = useState(null);
  const [activeHistorySessionId, setActiveHistorySessionId] = useState(null);
  const [isSessionMessagesLoading, setIsSessionMessagesLoading] = useState(false);
  const [sessionMessagesError, setSessionMessagesError] = useState('');
  const [sessionMessagesRetryable, setSessionMessagesRetryable] = useState(false);
  const [queryIntentDone, setQueryIntentDone] = useState(false);
  const [plannerSteps, setPlannerSteps] = useState([]);

  const [sessionHistory, setSessionHistory] = useState(() => sessionContext?.sessionHistory || []);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const [recentMenuOpenId, setRecentMenuOpenId] = useState(null);
  const [pinLoadingById, setPinLoadingById] = useState({});
  const [deleteTargetSessionId, setDeleteTargetSessionId] = useState(null);
  const [isDeletingSession, setIsDeletingSession] = useState(false);

  const [messageFeedback, setMessageFeedback] = useState({});
  const [feedbackFormOpen, setFeedbackFormOpen] = useState(null);
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackError, setFeedbackError] = useState('');
  const [feedbackSubmitPendingByMessage, setFeedbackSubmitPendingByMessage] = useState({});
  const [submittedFeedbackByMessage, setSubmittedFeedbackByMessage] = useState({});
  const [feedbackToasts, setFeedbackToasts] = useState([]);

  const sseAbortRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const sessionMessagesRequestIdRef = useRef(0);

  const displaySessionHistory = sessionHistory;

  const getFeedbackMessageKey = (messageIndex, messageData) => {
    const scopedSessionId = sessionId || activeHistorySessionId || 'unknown';
    const scopedMessageId = messageData?.assistant_message_id || messageData?.queryId || messageData?.id || `idx-${messageIndex}`;
    return `${scopedMessageId}`;
  };

  const pushFeedbackToast = (type, text, duration = 6000) => {
    setFeedbackToasts((prev) => [
      ...prev,
      { id: ++toastIdCounter, duration, text, type },
    ]);
  };

  const removeFeedbackToast = (toastId) => {
    setFeedbackToasts((prev) => prev.filter((toast) => toast.id !== toastId));
  };

  const handleFeedbackTextChange = (text) => {
    setFeedbackText(text);
    if (text.length >= 500) {
      setFeedbackError('Comment must be 500 characters or fewer.');
    } else {
      setFeedbackError('');
    }
  };

  const handleMessageFeedback = (messageIndex, feedbackType, messageData) => {
    const feedbackKey = getFeedbackMessageKey(messageIndex, messageData);
    if (feedbackSubmitPendingByMessage[feedbackKey]) return;

    // If already submitted and user clicks the SAME thumb → reset feedback
    if (submittedFeedbackByMessage[feedbackKey]) {
      const submittedThumb = submittedFeedbackByMessage[feedbackKey].thumbs;
      const clickedThumb = feedbackType === 'up' ? 'like' : 'unlike';
      if (submittedThumb === clickedThumb) {
        handleResetFeedback(messageIndex, messageData, feedbackType);
        return;
      }
      // Clicking the other thumb is disabled in the UI, but guard here too
      return;
    }

    const currentFeedback = messageFeedback[messageIndex] || null;
    const nextFeedback = currentFeedback === feedbackType ? null : feedbackType;
    setMessageFeedback((prev) => ({ ...prev, [messageIndex]: nextFeedback }));
    setFeedbackError('');
    setFeedbackText('');

    if (feedbackType === 'up') {
      // Thumbs up: submit directly without opening the form
      if (nextFeedback) {
        handleSubmitFeedback(messageIndex, messageData, 'up');
      }
    } else {
      // Thumbs down: open form as usual
      setFeedbackFormOpen(nextFeedback ? messageIndex : null);
    }
  };

  const handleResetFeedback = async (messageIndex, messageData, feedbackType) => {
    const feedbackKey = getFeedbackMessageKey(messageIndex, messageData);
    if (feedbackSubmitPendingByMessage[feedbackKey]) return;

    const scopedSessionId = sessionId || activeHistorySessionId;
    if (!scopedSessionId) return;

    const queryId = messageData?.queryId || messageData?.id || null;
    if (!queryId || typeof queryId !== 'string') return;

    const selectedFeedback = feedbackType || messageFeedback[messageIndex] || null;
    const payload = {
      message_id: feedbackKey || null,
      query_id: queryId.replace("-assistant",""),
      session_id: scopedSessionId,
      agent_id: 'RISI',
      thumbs: selectedFeedback === 'up' ? 'like' : 'unlike',
      feedback_text: '',
      set_feedback: false,
    };

    setFeedbackSubmitPendingByMessage((prev) => ({ ...prev, [feedbackKey]: true }));

    try {
      const { Authorization } = await getAuthHeader();
      const res = await fetch(
        `${appConstants.MI_AI_BASE_URL}/api/v1/feedback`,
        {
          method: 'POST',
          headers: { Authorization, Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        },
      );

      if (res.ok) {
        setSubmittedFeedbackByMessage((prev) => {
          const next = { ...prev };
          delete next[feedbackKey];
          return next;
        });
        setMessageFeedback((prev) => {
          const next = { ...prev };
          delete next[messageIndex];
          return next;
        });
        pushFeedbackToast('success', 'Feedback has been reset');
      } else {
        pushFeedbackToast('error', 'Unable to reset feedback. Please try again.');
      }
    } catch (err) {
      console.error('Feedback reset error:', err);
      pushFeedbackToast('error', 'Unable to reset feedback. Please check your connection.');
    } finally {
      setFeedbackSubmitPendingByMessage((prev) => {
        const next = { ...prev };
        delete next[feedbackKey];
        return next;
      });
    }
  };

  const handleCloseFeedbackForm = () => {
    setFeedbackFormOpen(null);
    setFeedbackError('');
    setFeedbackText('');
  };

  const handleSubmitFeedback = async (messageIndex, messageData, feedbackType) => {
    const selectedFeedback = feedbackType || messageFeedback[messageIndex] || null;
    if (!selectedFeedback) {
      setFeedbackError('Select thumbs up or thumbs down to continue.');
      return;
    }

    const feedbackKey = getFeedbackMessageKey(messageIndex, messageData);
    if (feedbackSubmitPendingByMessage[feedbackKey]) return;
    if (submittedFeedbackByMessage[feedbackKey]) {
      setFeedbackError('');
      pushFeedbackToast('info', 'Feedback already submitted for this response.');
      return;
    }

    const scopedSessionId = sessionId || activeHistorySessionId;
    if (!scopedSessionId) {
      setFeedbackError('Unable to submit feedback right now. Missing session.');
      pushFeedbackToast('error', 'Unable to submit feedback right now. Please retry.');
      return;
    }

    const queryId = messageData?.queryId || messageData?.id || null;
    if (!queryId || typeof queryId !== 'string') {
      setFeedbackError('Unable to submit feedback for this response.');
      pushFeedbackToast('error', 'Unable to submit feedback for this response. Please retry.');
      return;
    }

    const trimmedComment = feedbackText.trim();
    if (trimmedComment.length > 500) {
      setFeedbackError('Comment must be 500 characters or fewer.');
      return;
    }

    const payload = {
      message_id: feedbackKey || null,
      query_id: queryId.replace("-assistant",""),
      session_id: scopedSessionId,
      agent_id: 'RISI',
      thumbs: selectedFeedback === 'up' ? 'like' : 'unlike',
      feedback_text: trimmedComment || '',
      set_feedback: true,
    };

    const params = new URLSearchParams();
    if (threadId) params.set('conversation_id', threadId);

    setFeedbackSubmitPendingByMessage((prev) => ({ ...prev, [feedbackKey]: true }));
    setFeedbackError('');

    try {
      const { Authorization } = await getAuthHeader();
      const url = `${appConstants.MI_AI_BASE_URL}/api/v1/feedback`;
      const res = await fetch(
        url,
        {
          method: 'POST',
          headers: { Authorization, Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        },
      );

      let responseData = null;
      try { responseData = await res.json(); } catch (_) { responseData = null; }

      if (!res.ok) {
        if (res.status === 400) {
          const validationMessage = responseData?.message || 'Please check your feedback input and try again.';
          setFeedbackError(validationMessage);
          pushFeedbackToast('warning', validationMessage);
          return;
        }
        const errorMessage = responseData?.message || 'Unable to save feedback right now. Please try again.';
        setFeedbackError(errorMessage);
        pushFeedbackToast('error', errorMessage);
        return;
      }

      setSubmittedFeedbackByMessage((prev) => ({
        ...prev,
        [feedbackKey]: {
          message_id: responseData?.message_id || payload.message_id || null,
          query_id: responseData?.query_id || payload.query_id,
          session_id: responseData?.session_id || payload.session_id,
          agent_id: payload.agent_id,
          thumbs: responseData?.thumbs || payload.thumbs,
          feedback_text: responseData?.feedback_text || payload.feedback_text || '',
          created_at: responseData?.created_at || new Date().toISOString(),
        },
      }));

      handleCloseFeedbackForm();
      pushFeedbackToast('success', 'Feedback submitted. Thank you!');
    } catch (err) {
      console.error('Feedback submit error:', err);
      const networkMessage = 'Unable to save feedback right now. Please check your connection and retry.';
      setFeedbackError(networkMessage);
      pushFeedbackToast('error', networkMessage);
    } finally {
      setFeedbackSubmitPendingByMessage((prev) => {
        const next = { ...prev };
        delete next[feedbackKey];
        return next;
      });
    }
  };

  const createSession = async (title) => {
    const { Authorization } = await getAuthHeader();
    const gpid = localStorage.getItem('gpid') ? atob(localStorage.getItem('gpid')) : '';
    const res = await fetch(`${appConstants.MI_AI_BASE_URL}/api/v1/chats?chatbot=mi`, {
      method: 'POST',
      headers: { Authorization, 'Content-Type': 'application/json' },
      body: JSON.stringify({ title: title || '' }),
    });
    if (!res.ok) throw new Error(`Session creation failed: ${res.status}`);
    return res.json();
  };

  const openSSEStream = async (sid, text, tid) => {
    if (sseAbortRef.current) sseAbortRef.current.abort();
    const controller = new AbortController();
    sseAbortRef.current = controller;

    setShowRightSidebar(false);
    setMessages((prev) => [...prev, { type: 'user', text }]);
    setIsThinking(true);
    setQueryIntentDone(false);
    setPlannerSteps([]);
    setElapsedSeconds(0);

    const { Authorization } = await getAuthHeader();
    const gpid = localStorage.getItem('gpid') ? atob(localStorage.getItem('gpid')) : '';

    fetch(`${appConstants.MI_AI_BASE_URL}/api/v1/query`, {
      method: 'POST',
      headers: { Authorization, 'Content-Type': 'application/json', 'Accept': 'text/event-stream' },
      signal: controller.signal,
      body: JSON.stringify({
        query: text,
        session_id: sid,
        chat_id: tid || sid,
      }),
    })
      .then(async (res) => {
        if (!res.ok || !res.body) {
          setMessages((prev) => [
            ...prev,
            { type: 'ai', response: 'Something went wrong. Please try again.', followUps: [] },
          ]);
          setIsThinking(false);
          return;
        }
        const reader = res.body.getReader();
        let buffer = '';
        const decoder = new TextDecoder();

        while (true) 
        {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          const parts = buffer.split('\n\n');

          buffer = parts.pop() ?? '';
          for (const part of parts) 
          {
            if (!part.trim()) continue;

            let eventName = 'message';

            let dataStr = '';
            for (const line of part.split('\n')) {
              if (line.startsWith('event:')) eventName = line.slice(6).trim();
              else if (line.startsWith('data:')) dataStr += line.slice(5).trim();
            }
            if (!dataStr) continue;
            try {
              const eventData = JSON.parse(dataStr);
              switch (eventName) {
                case 'planner_info':
                  setPlannerSteps((prev) => [
                    ...prev,
                    { description: eventData.message || eventData.thought || eventData.stage || '', status: 'complete' },
                  ]);
                  break;
                case 'planner_reasoning_step':
                  setPlannerSteps((prev) => [
                    ...prev,
                    { description: eventData.thought || eventData.step || '', status: 'complete' },
                  ]);
                  break;
                case 'query_complete':
                case 'done':
                  setQueryIntentDone(true);
                  break;
                case 'final_response': {
                  const { insight, responseWithoutInsights } = extractInsightsBlock(eventData.response || '');
                  const citations = Array.isArray(eventData.citations)
                    ? eventData.citations
                        .filter((c) => c && typeof c === 'object')
                        .map((c) => ({ file_name: c.file_name || '', url: c.url || c.source || '' }))
                    : [];
                  const sqlQueries = Array.isArray(eventData.sql_queries)
                    ? eventData.sql_queries
                        .filter((q) => typeof q === 'string' && q.trim())
                        .map((q) => q.trim())
                    : [];
                  setMessages((prev) => [
                    ...prev,
                    {
                      id: eventData.assistant_message_id || eventData.query_id || eventData.queryId || `assistant-${Date.now()}`,
                      queryId: eventData.assistant_message_id || eventData.query_id || eventData.queryId || null,
                      messageId: eventData.assistant_message_id || eventData.message_id || eventData.messageId || null,
                      type: 'ai',
                      lastRefreshed: eventData.last_refreshed || null,
                      insight,
                      response: responseWithoutInsights,
                      followUps: eventData.follow_up_questions || [],
                      chartConfig: eventData.chart_config || null,
                      genieRawData: Array.isArray(eventData.genie_raw_data) ? eventData.genie_raw_data : null,
                      citations,
                      sqlQueries,
                      sourcesCount: citations.length + sqlQueries.length,
                    },
                  ]);
                  if (citations.length > 0 || sqlQueries.length > 0) {
                    setActiveCitations(citations);
                    setActiveSqlQueries(sqlQueries);
                    setExpandedSourceItems({});
                  } else {
                    setActiveCitations([]);
                    setActiveSqlQueries([]);
                    setExpandedSourceItems({});
                  }
                  setIsThinking(false);
                  break;
                }
                // case 'query_error':
                case 'error':
                  setMessages((prev) => [
                    ...prev,
                    { type: 'ai', response: eventData.error || eventData.message || 'Something went wrong. Please try again.', followUps: [], isError: true },
                  ]);
                  setIsThinking(false);
                  break;
                default:
                  break;
              }
            } catch (_e) {
              // ignore non-JSON event data
            }
          }
        }
        // Fallback: stop thinking if stream ends without final_response
        setIsThinking(false);
      })
      .catch((err) => {
        if (err.name !== 'AbortError') {
          console.error('SSE error:', err);
          setIsThinking(false);
          setMessages((prev) => [
            ...prev,
            {
              type: 'ai',
              response: 'A network error occurred while processing your request. Please check your connection and try again.',
              followUps: [],
              isError: true,
            },
          ]);
        }
      });
  };

  const fireMessage = async (text, sid, tid) => {
    setMessages((prev) => [...prev, { type: 'user', text }]);
    setIsThinking(true);
    setQueryIntentDone(false);
    setPlannerSteps([]);
    setElapsedSeconds(0);

    // Background-only call — response and UI update come via openSSEStream (final_response event)
    try {
      const { Authorization } = await getAuthHeader();
      const gpid = localStorage.getItem('gpid') ? atob(localStorage.getItem('gpid')) : '';
      const body = { query: text };
      if (sid) body.session_id = sid;
      if (tid) body.thread_id = tid;
      await fetch(`${appConstants.MI_AI_BASE_URL}/api/v1/query`, {
        method: 'POST',
        headers: { Authorization, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      // Commented out: UI response was previously handled here before switching to SSE (final_response event)
      // if (!res.ok) throw new Error(`Query failed: ${res.status}`);
      // const data = await res.json();
      // const sqlQueries = Array.isArray(data.sql_queries)
      //   ? data.sql_queries
      //       .filter((q) => typeof q === 'string' && q.trim())
      //       .map((q) => q.trim())
      //   : [];
      // const { insight, responseWithoutInsights } = extractInsightsBlock(data.response || '');
      // setMessages((prev) => [
      //   ...prev,
      //   {
      //     id: data.query_id || data.queryId || `assistant-${Date.now()}`,
      //     queryId: data.query_id || data.queryId || null,
      //     messageId: data.message_id || data.messageId || null,
      //     type: 'ai',
      //     metadata: `Data As On: ${new Date().toLocaleDateString('en-US', {
      //       month: 'short',
      //       day: 'numeric',
      //       year: 'numeric',
      //     })}`,
      //     insight,
      //     response: responseWithoutInsights,
      //     followUps: data.follow_up_questions || [],
      //     chartConfig: data.chart_config || null,
      //     sqlQueries,
      //     sourcesCount: sqlQueries.length,
      //   },
      // ]);
    } catch (err) {
      console.error('Background query error:', err);
      // setMessages((prev) => [
      //   ...prev,
      //   { type: 'ai', response: 'Something went wrong. Please try again.', followUps: [] },
      // ]);
    }
    // finally {
    //   setIsThinking(false); // now handled by openSSEStream on final_response / stream end
    // }
  };

  const handleSend = async () => {
    if (!message.trim() || isThinking) return;
    const text = message.trim();
    setMessage('');

    let sid = sessionId;
    let tid = threadId;

    if (!sid) {
      try {
        const session = await createSession(text);
        sid = session.sessionId;
        tid = session.conversationId;
        setSessionId(sid);
        setThreadId(tid);

        // Persist session_id so a page refresh reloads the session instead of going blank
        navigate(location.pathname, {
          replace: true,
          state: { session_id: sid, conversation_id: tid },
        });

        const tempSession = {
          sessionId: sid,
          conversationId: '',
          title: text,
          lastMessage: text,
          messageCount: 0,
          isPinned: false,
          pinnedAt: null,
          createdAt: new Date().toISOString(),
          lastActiveAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        setSessionHistory((prev) => [tempSession, ...prev]);
        fetchSessionHistory();
      } catch (err) {
        console.error('Session creation failed:', err);
        return;
      }
    }

    openSSEStream(sid, text, tid);
    // await fireMessage(text, sid, tid); // Non-Streaming API call
  };

  /* istanbul ignore next */
  useEffect(() => {
    return () => {
      dispatch({
        type: 'RESET_USER_INFO_ACTION',
        payload: 'RESET_USER_INFO_ACTION',
      });
    };
  }, []);

  /* istanbul ignore next */
  useEffect(() => {
    async function fetchUserInfo() {
      const user = await getUserClaims();
      if (user) {
        setIsUserDataFetched(true);
        localStorage.setItem('gpid', btoa(JSON.stringify(user?.gpid)));
        setUserInfo(user);
        dispatch(FetchUserInfoAction(user));
      }
    }
    setIsDataLoading(true);
    fetchUserInfo();

  }, []);


  // Thinking timer
  useEffect(() => {
    if (!isThinking) return;
    const interval = setInterval(() => {
      setElapsedSeconds((prev) => prev + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [isThinking]);

  // Cleanup SSE on unmount
  useEffect(() => {
    return () => {
      if (sseAbortRef.current) sseAbortRef.current.abort();
    };
  }, []);

  // Auto-scroll messages
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
  }, [messages, isThinking, plannerSteps]);

  // Sync active session id
  useEffect(() => {
    if (sessionId) setActiveHistorySessionId(sessionId);
  }, [sessionId]);

  // Fetch session history when sidebar opens
  useEffect(() => {
    if (showSidebar) {
      fetchSessionHistory().then((history) => {
        if (
          !hasNavigationState &&
          !activeHistorySessionId &&
          history &&
          history.length > 0
        ) {
          loadSessionMessages(history[0].sessionId, history[0].conversationId);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSidebar, location.key]);

  // Handle navigation state: load session or fire initial message
  useEffect(() => {
    // A REPLACE navigation is fired internally to persist session_id after creation — ignore it
    if (navigationType === 'REPLACE') return;
    if (sseAbortRef.current) sseAbortRef.current.abort();
    setThreadId(null);
    setElapsedSeconds(0);
    setSessionId(null);
    setIsThinking(false);
    setPlannerSteps([]);
    setQueryIntentDone(false);

    if (!hasNavigationState) {
      setLastProcessedMessage(null);

      setMessages([]);
      return;
    }

    if (navigationType === 'POP') {
      if (initialSessionId) {
        loadSessionMessages(initialSessionId, initialThreadId);

        return;
      }
      setLastProcessedMessage(null);
      setMessages([]);
      return;
    }

    if (!initialMessage && initialSessionId) {
      loadSessionMessages(initialSessionId, initialThreadId);
      setLastProcessedMessage(null);
      return;
    }

    if (!initialMessage) {
      setMessages([]);
      setLastProcessedMessage(null);
      return;
    }

    if (lastProcessedMessage === initialMessage) {
      return;
    }

    setMessages([]);

    let isCancelled = false;
    
    setMessage('');
    (async () => {
      try {
        let sid = initialSessionId;
        let tid = initialThreadId;

        if (!sid) {
          const session = await createSession(initialMessage);

          sid = session.sessionId;
          tid = session.conversationId;
        }

        if (isCancelled) {
          return;
        }
        setSessionId(sid);
        setActiveHistorySessionId(sid);
        setThreadId(tid);

        setLastProcessedMessage(initialMessage);

        // Persist session_id so a page refresh reloads the session instead of going blank
        navigate(location.pathname, {
          replace: true,
          state: { session_id: sid, conversation_id: tid, message: initialMessage },
        });

        setSessionHistory((prev) => {
          if (prev.some((s) => s.sessionId === sid)) return prev;
          return [
            {
              sessionId: sid,
              conversationId: '',
              title: initialMessage,
              lastMessage: initialMessage,
              messageCount: 0,
              isPinned: false,
              pinnedAt: null,
              createdAt: new Date().toISOString(),
              lastActiveAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            },
            ...prev,
          ];
        });

        openSSEStream(sid, initialMessage, tid);
        // await fireMessage(initialMessage, sid, tid); // Non-Streaming API call
      } catch (err) {
        if (!isCancelled) {
          console.error('Session bootstrap failed:', err);
          setMessages((prev) => [
            ...prev,
            {
              type: 'ai',
              response: 'Something went wrong. Please try again.',
              followUps: [],
            },
          ]);
        }
      }
    })();

    return () => {
      isCancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    location.key,
    navigationType,
    hasNavigationState,
    initialMessage,
    initialSessionId,
    initialThreadId,
  ]);

  const closeRecentMenu = () => setRecentMenuOpenId(null);

  const fetchSessionHistory = async () => {
    setIsHistoryLoading(true);
    setHistoryError('');
    try {
      const { Authorization } = await getAuthHeader();
      const gpid = localStorage.getItem('gpid') ? atob(localStorage.getItem('gpid')) : '';
      const res = await fetch(
        `${appConstants.MI_AI_BASE_URL}/api/v1/chats?chatbot=mi`,
        {
          method: 'GET',
          headers: { Authorization, 'Content-Type': 'application/json' },
        },
      );
      if (!res.ok) {
        throw new Error(`Session history failed: ${res.status}`);
      }
      const data = await res.json();
      const normalized = normalizeSessionHistory(data);
      setSessionHistory(prev => {
        const serverIds = new Set(normalized.map(s => s.sessionId));
        const localOnly = prev.filter(s => !serverIds.has(s.sessionId));
        const merged = [...localOnly, ...normalized];
        return merged.sort((a, b) => {
          if (a.isPinned !== b.isPinned) return a.isPinned ? -1 : 1;
          return new Date(b.lastActiveAt || 0) - new Date(a.lastActiveAt || 0);
        });
      });
      if (sessionContext?.setSessionHistory) {
        sessionContext.setSessionHistory(normalized);
      }
      return normalized;
    } catch (err) {
      console.error('Session history error:', err);
      setSessionHistory([]);
      setHistoryError('Unable to load sessions.');
      return [];
    } finally {
      setIsHistoryLoading(false);
    }
  };

  const loadSessionMessages = async (selectedSessionId, selectedConversationId) => {
    if (!selectedSessionId) return;

    if (sseAbortRef.current) sseAbortRef.current.abort();
    setPlannerSteps([]);
    setQueryIntentDone(false);
    setIsThinking(false);

    const requestId = ++sessionMessagesRequestIdRef.current;
    setShowRightSidebar(false);
    setActiveHistorySessionId(selectedSessionId);
    setSessionId(selectedSessionId);
    setThreadId(selectedConversationId || null);
    setMessages([]);
    setIsSessionMessagesLoading(true);
    setSessionMessagesError('');
    setSessionMessagesRetryable(false);

    try {
      const { Authorization } = await getAuthHeader();
      const gpid = localStorage.getItem('gpid') ? atob(localStorage.getItem('gpid')) : '';
      const res = await fetch(
        `${appConstants.MI_AI_BASE_URL}/api/v1/chats/${selectedSessionId}/messages`,
        {
          method: 'GET',
          headers: { Authorization, 'Content-Type': 'application/json' },
        },
      );

      if (!res.ok) {
        setSessionMessagesRetryable(res.status === 503 || res.status === 504);
        throw new Error(`Session messages failed: ${res.status}`);
      }

      const data = await res.json();
      if (requestId !== sessionMessagesRequestIdRef.current) return;
      const normalized = normalizeSessionMessages(data);
      setMessages(normalized);

      // Pre-populate feedback state from history messages
      const historyFeedback = {};
      const historySubmitted = {};
      normalized.forEach((msg, idx) => {
        if (msg.type === 'ai' && msg.isFeedbackActive && msg.feedbackThumbs) {
          const thumbDir = msg.feedbackThumbs === 'like' ? 'up' : 'down';
          historyFeedback[idx] = thumbDir;
          const key = getFeedbackMessageKey(idx, msg);
          historySubmitted[key] = { thumbs: msg.feedbackThumbs };
        }
      });
      setMessageFeedback(historyFeedback);
      setSubmittedFeedbackByMessage(historySubmitted);
      setFeedbackFormOpen(null);

      const lastAiMsg = [...normalized].reverse().find((m) => m.type === 'ai');
      const lastCitations = Array.isArray(lastAiMsg?.citations) ? lastAiMsg.citations : [];
      const lastSqlQueries = Array.isArray(lastAiMsg?.sqlQueries) ? lastAiMsg.sqlQueries : [];
      if (lastCitations.length > 0 || lastSqlQueries.length > 0) {
        setActiveCitations(lastCitations);
        setActiveSqlQueries(lastSqlQueries);
        setExpandedSourceItems({});
      } else {
        setActiveCitations([]);
        setActiveSqlQueries([]);
        setExpandedSourceItems({});
      }
    } catch (err) {
      if (requestId !== sessionMessagesRequestIdRef.current) return;
      console.error('Session messages error:', err);
      setMessages([]);
      setSessionMessagesError('Unable to load messages. Please retry.');
    } finally {
      if (requestId === sessionMessagesRequestIdRef.current) {
        setIsSessionMessagesLoading(false);
      }
    }
  };

  const handleSendMessage = () => handleSend();

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="mi-chat-container">
      <div className="mi-subheader-2">
        <div className="mi-subheader-left">
          <button
            onClick={() => navigate('/ai-assistant/market-intelligence')}
            className="mi-back-btn"
            style={{ color: '#006FCF' }}
          >
            <Icon icon="arrow_back" style={{ color: '#006FCF' }} />
          </button>
          <button
            onClick={() => setShowSidebar((prev) => !prev)}
            className="mi-sidebar-toggle-btn"
            style={{ marginLeft: '6px' }}
          >
            <Icon icon="view_sidebar" style={{ color: '#006FCF' }} />
          </button>
          <div className="mi-agent-badge" style={{ marginLeft: '6px' }}>MI</div>
          <span
            className="mi-agent-name"
            style={{ marginLeft: '8px', cursor: 'pointer' }}
            
            tabIndex={0}
            role="button"
            onClick={() => setShowAgentMenu((prev) => !prev)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setShowAgentMenu((prev) => !prev); }}
          >
            Market Intelligence
          </span>
          <Icon
            icon="arrow_drop_down"
            style={{ cursor: 'pointer' }}
            onClick={() => setShowAgentMenu((prev) => !prev)}
          />
        </div>

        <div className="mi-subheader-right">
          <button
            className="mi-right-sidebar-toggle-btn mi-sidebar-toggle-btn"
            onClick={() => setShowRightSidebar((prev) => !prev)}
          >
            <Icon icon="view_sidebar" style={{ color: '#006FCF' }} />
          </button>
        </div>
      </div>

      {showAgentMenu && (
        <MenuList
          className="ph-agent-select"
          customNoResultMessage=""
          emptyStateMessage=""
          height={240}
          hideMenuListOnSelection
          isClearable
          items={[
            {
              id: '/ai-assistant/knowledge-management',
              label: 'Knowledge Management',
            },
            ...(getSpendAnalyticsCardVisibilityFromEnv() ? [{
              id: '/spend-analytics-ai-agent',
              label: 'Spend Analytics',
            }] : []),
          ]}
          listHeaderLabel=""
          onHideMenuList={() => setShowAgentMenu(false)}
          selectedItemCallback={function S8e(item) {
            if (item?.[0]?.id) {
              navigate(item[0].id);
            }
          }}
          selectedItems={[]}
          size="medium"
          style={{}}
          type="single"
          width=""
          showSearch={false}
        />
      )}

      <div className="mi-chat-body">
        {showSidebar && (
          <div className="mi-chat-sidebar">
            <div className="mi-chats-header">
              <span>Chats</span>
              <button
                className="mi-close-sidebar"
                onClick={() => setShowSidebar(false)}
              >
                <Icon icon="close" />
              </button>
            </div>
            <button
              className="mi-new-chat-btn"
              onClick={() => navigate('/ai-assistant/market-intelligence')}
            >
              <span className="mi-new-chat-plus">+</span>
              <span>New chat</span>
            </button>
            <div className="mi-recents">
              <div className="mi-recents-label">Recents</div>
              {isHistoryLoading && (
                <div className="mi-recent-item">Loading sessions...</div>
              )}
              {!isHistoryLoading && historyError && (
                <div className="mi-recent-item">{historyError}</div>
              )}
              {!isHistoryLoading &&
                !historyError &&
                displaySessionHistory.length === 0 && (
                  <div className="mi-recent-item">No sessions</div>
                )}
              {!isHistoryLoading &&
                !historyError &&
                displaySessionHistory.map((chat) => (
                  <div key={chat.sessionId} className="sa-conv-recent-row">
                    <button
                      className={`mi-recent-item sa-conv-recent-item${chat.sessionId === activeHistorySessionId ? ' mi-conv-recent-active' : ''}`}
                      onClick={() => loadSessionMessages(chat.sessionId, chat.conversationId)}
                      title={chat.title}
                    >
                      <div className="sa-conv-recent-title-wrap" style={{ width: '100%', overflow: 'hidden' }}>
                        <span className="sa-conv-recent-title" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', maxWidth: '100%' }}>
                          {chat.title}
                        </span>
                      </div>
                      {/* {chat.isPinned && (
                        <span className="sa-conv-recent-pinned-badge">
                          Pinned
                        </span>
                      )} */}
                    </button>
                    {/* <button
                      type="button"
                      className="sa-conv-recent-actions-trigger"
                      aria-label="Session actions"
                      title="Session actions"
                      onClick={(e) => {
                        e.stopPropagation();
                        setRecentMenuOpenId((prev) =>
                          prev === chat.sessionId ? null : chat.sessionId,
                        );
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 18 18"
                        fill="none"
                        aria-hidden="true"
                      >
                        <path
                          d="M9 3.25C9.69036 3.25 10.25 2.69036 10.25 2C10.25 1.30964 9.69036 0.75 9 0.75C8.30964 0.75 7.75 1.30964 7.75 2C7.75 2.69036 8.30964 3.25 9 3.25Z"
                          fill="#4B4B4B"
                        />
                        <path
                          d="M9 10.25C9.69036 10.25 10.25 9.69036 10.25 9C10.25 8.30964 9.69036 7.75 9 7.75C8.30964 7.75 7.75 8.30964 7.75 9C7.75 9.69036 8.30964 10.25 9 10.25Z"
                          fill="#4B4B4B"
                        />
                        <path
                          d="M9 17.25C9.69036 17.25 10.25 16.6904 10.25 16C10.25 15.3096 9.69036 14.75 9 14.75C8.30964 14.75 7.75 15.3096 7.75 16C7.75 16.6904 8.30964 17.25 9 17.25Z"
                          fill="#4B4B4B"
                        />
                      </svg>
                    </button> */}
                    {/* {recentMenuOpenId === chat.sessionId && (
                      <div className="sa-conv-recent-actions-menu" role="menu">
                        <button
                          type="button"
                          className="sa-conv-recent-actions-item"
                          disabled={Boolean(pinLoadingById[chat.sessionId])}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleTogglePinSession(chat);
                          }}
                        >
                          {pinLoadingById[chat.sessionId]
                            ? 'Updating...'
                            : chat.isPinned
                              ? 'Unpin'
                              : 'Pin'}
                        </button>
                        <button
                          type="button"
                          className="sa-conv-recent-actions-item sa-conv-recent-actions-item--danger"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteTargetSessionId(chat.sessionId);
                            closeRecentMenu();
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    )} */}
                  </div>
                ))}
            </div>
          </div>
        )}

        <div className="mi-chat-main mi-chat-main--active">
          {isSessionMessagesLoading && (
            <div className="mi-recent-item" style={{ padding: '24px', textAlign: 'center' }}>Loading messages...</div>
          )}
          {!isSessionMessagesLoading && sessionMessagesError && (
            <div className="mi-recent-item" style={{ padding: '24px', color: '#c00' }}>
              <span>{sessionMessagesError}</span>
              {sessionMessagesRetryable && activeHistorySessionId && (
                <button
                  type="button"
                  style={{ marginLeft: '8px' }}
                  onClick={() => loadSessionMessages(activeHistorySessionId, threadId)}
                >
                  Retry
                </button>
              )}
            </div>
          )}
          {!isSessionMessagesLoading && !sessionMessagesError && (
            <MIChatActiveView
              chatbot="mi"
              messages={messages}
              isThinking={isThinking}
              queryIntentDone={queryIntentDone}
              plannerSteps={plannerSteps}
              elapsedSeconds={elapsedSeconds}
              message={message}
              setMessage={setMessage}
              handleKeyPress={handleKeyPress}
              handleSendMessage={handleSendMessage}
              messagesContainerRef={messagesContainerRef}
              onShowSources={({ sqlQueries: qs, citations: cits }) => {
                setActiveSqlQueries(Array.isArray(qs) ? qs : []);
                setActiveCitations(Array.isArray(cits) ? cits : []);
                setExpandedSourceItems({});
                setShowRightSidebar(true);
              }}
              messageFeedback={messageFeedback}
              feedbackFormOpen={feedbackFormOpen}
              feedbackText={feedbackText}
              feedbackError={feedbackError}
              feedbackSubmitPendingByMessage={feedbackSubmitPendingByMessage}
              submittedFeedbackByMessage={submittedFeedbackByMessage}
              getFeedbackMessageKey={getFeedbackMessageKey}
              onMessageFeedback={handleMessageFeedback}
              onFeedbackTextChange={handleFeedbackTextChange}
              onFeedbackCancel={handleCloseFeedbackForm}
              onFeedbackSubmit={handleSubmitFeedback}
            />
          )}
        </div>

        {showRightSidebar && (
          <div className="mi-chat-sidebar mi-chat-sidebar--right">
            <div className="mi-chats-header">
              <span>Sources</span>
              <button
                className="mi-close-sidebar"
                onClick={() => setShowRightSidebar(false)}
              >
                <Icon icon="close" />
              </button>
            </div>
            <div className="mi-conv-sources-list">
              {activeCitations.length === 0 && activeSqlQueries.length === 0 && (
                <div className="sa-conv-sources-empty">No sources available.</div>
              )}
              {activeCitations.map((citation, index) => (
                <CitationItem
                  key={`citation-${index}-${citation.url}`}
                  citation={citation}
                  index={index}
                />
              ))}
              {activeSqlQueries.map((sourceText, index) => (
                <SourceQueryItem
                  key={`${index}-${sourceText.slice(0, 40)}`}
                  sourceText={sourceText}
                  index={activeCitations.length + index}
                  isExpanded={Boolean(expandedSourceItems[index])}
                  onToggle={() =>
                    setExpandedSourceItems((prev) => ({
                      ...prev,
                      [index]: !prev[index],
                    }))
                  }
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* {deleteTargetSessionId && (
        <div className="sa-conv-delete-modal-backdrop" role="presentation">
          <div
            className="sa-conv-delete-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Delete chat"
          >
            <h3>Delete chat?</h3>
            <p>
              Are you sure you want to delete this chat? This action cannot be
              undone.
            </p>
            <div className="sa-conv-delete-modal-actions">
              <button
                type="button"
                onClick={() => setDeleteTargetSessionId(null)}
                disabled={isDeletingSession}
              >
                Cancel
              </button>
              <button
                type="button"
                className="sa-conv-delete-confirm-btn"
                onClick={handleConfirmDeleteSession}
                disabled={isDeletingSession}
              >
                {isDeletingSession ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )} */}

      <ToastContainer
        className="sa-conv-feedback-toast-container"
        style={{
          display: 'flex',
          width: '500px',
          maxWidth: '600px',
          padding: 'var(--Primitives-Spacing-spacing-8, 8px) var(--Primitives-Spacing-spacing-16, 16px)',
          alignItems: 'center',
          gap: '8px',
        }}
        data={feedbackToasts}
        position="bottom-center"
        showActionIcon={true}
        removeToast={removeFeedbackToast}
      />
    </div>
  );
};

export default MIConversations;
